import { Popover } from "radix-ui";
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ACTIVITY_TYPES, STATUS_EXPIRIES, type ActivityType, type PresenceStatusChoice, type StatusExpiry } from "@atrium/shared";
import { cn } from "../lib/cn";
import { relativeAlways } from "../lib/format";
import { activityLabel, presenceActions, STATUS_LABELS } from "../lib/presence";
import { useUI } from "../lib/uiStore";
import { useMe } from "../features/auth/AuthProvider";
import { useUpdateSettings } from "../features/settings/shared";
import { Button, IconButton } from "./ui/Button";
import { Switch } from "./ui/Controls";
import { Dialog } from "./ui/Dialog";
import { Input } from "./ui/Input";
import { Select } from "./ui/Settings";
import { PresenceIndicator } from "./PresenceIndicator";
import { IconChevronRight, IconGamepad, IconSmile, IconUser, IconX } from "./icons";

/*
 * Your status: online / idle / do not disturb / invisible, a custom status with
 * an emoji and an expiry, an activity ("Playing Chess"), and whether activity is
 * shared. Opened from the user panel and the account menu; the command palette
 * opens the custom status dialog directly.
 */

const CHOICES: { value: PresenceStatusChoice; hint: string }[] = [
  { value: "online", hint: "" },
  { value: "idle", hint: "Shown as away" },
  { value: "dnd", hint: "Mutes desktop notifications" },
  { value: "invisible", hint: "Appear offline, still use Atrium" },
];
const indicatorFor = (c: PresenceStatusChoice) => (c === "invisible" ? "offline" : c);

export const EXPIRY_LABELS: Record<StatusExpiry, string> = { "30m": "30 minutes", "1h": "1 hour", "4h": "4 hours", today: "Today", never: "Don't clear" };
const EXPIRY_OPTIONS = STATUS_EXPIRIES.map((v) => ({ value: v, label: EXPIRY_LABELS[v] }));
const ACTIVITY_LABELS: Record<ActivityType, string> = { playing: "Playing", listening: "Listening to", watching: "Watching", working: "Working on", custom: "Custom" };
const STATUS_EMOJIS = ["💬", "🎧", "💻", "📚", "☕", "🏠", "🚌", "🤒", "🌴", "🎮", "📅", "🌙"];

function expiresIn(iso: string | null | undefined) {
  return iso ? `Clears ${relativeAlways(iso)}` : null;
}

/** Popover with status choices; `children` is the trigger. */
export function StatusPicker({ children, side = "top", align = "start" }: { children: ReactNode; side?: "top" | "right" | "bottom"; align?: "start" | "end" }) {
  const me = useMe();
  const navigate = useNavigate();
  const updateSettings = useUpdateSettings();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<"custom" | "activity" | null>(null);
  const { status, customStatus, activity } = me.presence;

  const pick = (s: PresenceStatusChoice) => {
    setOpen(false);
    void presenceActions.setStatus(s);
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>{children}</Popover.Trigger>
        <Popover.Portal>
          <Popover.Content side={side} align={align} sideOffset={8} collisionPadding={8} className="z-[60] w-72 rounded-lg bg-surface p-1 shadow-pop motion-pop focus:outline-none" aria-label="Your status">
            <div role="radiogroup" aria-label="Status">
              {CHOICES.map((c) => (
                <button
                  key={c.value}
                  role="radio"
                  aria-checked={status === c.value}
                  onClick={() => pick(c.value)}
                  className={cn("flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left hover:bg-surface-2", status === c.value && "bg-surface-2")}
                >
                  <PresenceIndicator status={indicatorFor(c.value)} size="md" label={false} className="[--presence-ring:transparent]" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-fg">{STATUS_LABELS[c.value]}</span>
                    {c.hint && <span className="block text-xs text-fg-3">{c.hint}</span>}
                  </span>
                </button>
              ))}
            </div>
            <div className="-mx-1 my-1 h-px bg-line" />
            <StatusRow
              icon={IconSmile}
              title={customStatus ? `${customStatus.emoji ?? ""} ${customStatus.text}`.trim() : "Set a custom status"}
              sub={expiresIn(customStatus?.expiresAt)}
              onOpen={() => {
                setOpen(false);
                setDialog("custom");
              }}
              onClear={customStatus ? () => void presenceActions.clearCustomStatus() : undefined}
              clearLabel="Clear custom status"
            />
            <StatusRow
              icon={IconGamepad}
              title={activity ? activityLabel(activity) : "Set an activity"}
              sub={expiresIn(activity?.expiresAt)}
              onOpen={() => {
                setOpen(false);
                setDialog("activity");
              }}
              onClear={activity ? () => void presenceActions.clearActivity() : undefined}
              clearLabel="Clear activity"
            />
            <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 hover:bg-surface-2">
              <span className="flex-1 text-sm text-fg">Share my activity</span>
              <Switch label="Share my activity" checked={me.settings.shareActivity} onCheckedChange={(v) => void updateSettings({ shareActivity: v })} />
            </label>
            <div className="-mx-1 my-1 h-px bg-line" />
            <button
              onClick={() => {
                setOpen(false);
                navigate(`/u/${me.username}`);
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-fg hover:bg-surface-2"
            >
              <IconUser className="icon size-[18px] text-fg-2" aria-hidden />
              View profile
            </button>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <CustomStatusDialog open={dialog === "custom"} onOpenChange={(o) => setDialog(o ? "custom" : null)} />
      <ActivityDialog open={dialog === "activity"} onOpenChange={(o) => setDialog(o ? "activity" : null)} />
    </>
  );
}

function StatusRow({
  icon: I,
  title,
  sub,
  onOpen,
  onClear,
  clearLabel,
}: {
  icon: typeof IconSmile;
  title: string;
  sub: string | null;
  onOpen: () => void;
  onClear?: () => void;
  clearLabel: string;
}) {
  return (
    <div className="group flex items-center gap-1 rounded-md hover:bg-surface-2">
      <button onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-2 text-left">
        <I className="icon size-[18px] shrink-0 text-fg-2" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-fg">{title}</span>
          {sub && <span className="block truncate text-xs text-fg-3">{sub}</span>}
        </span>
        {!onClear && <IconChevronRight className="icon size-4 text-fg-3" aria-hidden />}
      </button>
      {onClear && <IconButton size="sm" label={clearLabel} icon={IconX} onClick={onClear} className="mr-1" />}
    </div>
  );
}

export function CustomStatusDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const me = useMe();
  const current = me.presence.customStatus;
  const [emoji, setEmoji] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [expiry, setExpiry] = useState<StatusExpiry>("today");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setEmoji(current?.emoji ?? null);
    setText(current?.text ?? "");
    setExpiry(current && !current.expiresAt ? "never" : "today");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    const ok = text.trim() || emoji ? await presenceActions.setCustomStatus({ text: text.trim(), emoji, expiresIn: expiry }) : await presenceActions.clearCustomStatus();
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Custom status"
      size="sm"
      footer={
        <>
          {current && (
            <Button variant="ghost" className="mr-auto" onClick={() => void presenceActions.clearCustomStatus().then((r) => r && onOpenChange(false))}>
              Clear
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="flex items-center gap-2">
          <span aria-hidden className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface text-xl">
            {emoji ?? <IconSmile className="icon text-fg-3" />}
          </span>
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={128} placeholder="What's happening?" aria-label="Status text" />
        </div>
        <div role="radiogroup" aria-label="Emoji" className="flex flex-wrap gap-1">
          {STATUS_EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              role="radio"
              aria-checked={emoji === e}
              aria-label={e}
              onClick={() => setEmoji(emoji === e ? null : e)}
              className={cn("flex size-9 items-center justify-center rounded-md text-lg transition-colors hover:bg-surface-2", emoji === e && "bg-accent-soft ring-1 ring-accent")}
            >
              {e}
            </button>
          ))}
        </div>
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm text-fg-2">Clear after</span>
          <Select<StatusExpiry> aria-label="Clear after" value={expiry} onChange={setExpiry} options={EXPIRY_OPTIONS} />
        </label>
      </form>
    </Dialog>
  );
}

export function ActivityDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const me = useMe();
  const current = me.presence.activity;
  const [type, setType] = useState<ActivityType>("playing");
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [expiry, setExpiry] = useState<StatusExpiry>("4h");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setType(current?.type ?? "playing");
    setName(current?.name ?? "");
    setDetails(current?.details ?? "");
    setExpiry(current && !current.expiresAt ? "never" : "4h");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const ok = await presenceActions.setActivity({ type, name: name.trim(), details: details.trim() || undefined, expiresIn: expiry });
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Activity"
      description={me.settings.shareActivity ? undefined : "Activity sharing is off, so only you will see this."}
      size="sm"
      footer={
        <>
          {current && (
            <Button variant="ghost" className="mr-auto" onClick={() => void presenceActions.clearActivity().then((r) => r && onOpenChange(false))}>
              Clear
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} disabled={!name.trim()} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="flex gap-2">
          <Select<ActivityType> aria-label="Activity type" value={type} onChange={setType} options={ACTIVITY_TYPES.map((t) => ({ value: t, label: ACTIVITY_LABELS[t] }))} className="w-40 shrink-0" />
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={128} placeholder={type === "custom" ? "What you're up to" : "Name"} aria-label="Activity name" />
        </div>
        <Input value={details} onChange={(e) => setDetails(e.target.value)} maxLength={128} placeholder="Details (optional)" aria-label="Details" />
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm text-fg-2">Clear after</span>
          <Select<StatusExpiry> aria-label="Clear activity after" value={expiry} onChange={setExpiry} options={EXPIRY_OPTIONS} />
        </label>
      </form>
    </Dialog>
  );
}

/** For the command palette's "Set custom status": mounted once in the shell. */
export function StatusDialogHost() {
  const overlay = useUI((s) => s.overlay);
  const setOverlay = useUI((s) => s.setOverlay);
  return <CustomStatusDialog open={overlay === "status"} onOpenChange={(o) => setOverlay(o ? "status" : null)} />;
}
